// src/geo2d/MapLibreTriple2D.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
// ✅ build（production）でのみ再現する “v is not defined” の典型原因：worker周り
// public/maplibre-gl-csp-worker.js を置いた上で production では必ず静的workerを使う
try {
  if (process.env.NODE_ENV === "production") {
    maplibregl.setWorkerUrl(
      (process.env.PUBLIC_URL || "") + "/maplibre-gl-csp-worker.js"
    );
  }
} catch {
  // 失敗してもアプリ自体は落とさない（ただし本番で地図が死ぬ可能性は高い）
}
function isMapStyleReady(map) {
  if (!map) return false;
  try {
    if (typeof map.isStyleLoaded === "function") return !!map.isStyleLoaded();
    if (typeof map.loaded === "function") return !!map.loaded();
  } catch {
    //
  }
  return false;
}
import "maplibre-gl/dist/maplibre-gl.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// 既存のメタデータを流用（あなたのプロジェクトに合わせてパス調整してください）
import { data_meta } from "../../importmetadata";

/**
 * ✅ 地理院Vectorのサンプルstyle.json（まずは動作確認優先）
 * - pale: 淡色
 * - std: 標準
 */
const GSI_STYLE_URL =
  "https://gsi-cyberjapan.github.io/gsivectortile-mapbox-gl-js/pale.json";

/** CSVのURL生成（参照のcsvfecthから移植） */
function buildCsvUrls(metadataNo, area, csv_name, year) {
  const areas = String(area).includes("&") ? String(area).split("&") : [String(area)];
  return areas.map((a) => `/csv/${metadataNo}/${a}_${csv_name}_${year}.csv`);
}

/** CSV fetch（複数URLを結合） */
async function fetchCombinedCsv(urls) {
  const texts = await Promise.all(
    urls.map(async (url) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status} ${url}`);
      return await r.text();
    })
  );
  return texts.join("");
}

/** CSV parse（\r\n / \n 両対応） */
function parseCsvToRows(csvText) {
  const lines = csvText
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  return lines.map((line) => line.split(","));
}

/**
 * ✅ 日本の標準地域メッシュ（JIS X 0410）: 2次(6桁)/3次(8桁)/4次(9桁) の bounds を返す
 * - return: { west, south, east, north }
 *
 * 2次(6): [LL][LL][a][b]
 * 3次(8): [LL][LL][a][b][c][d]
 * 4次(9): 3次 + [q]  (q=1..4)
 *
 * ※ここは「動く」ための最小実装（2/3/4次のみ）
 */
function meshCodeToBounds(code) {
  const s = String(code);
  if (![6, 8, 9].includes(s.length)) return null;

  const lat1 = Number(s.slice(0, 2)); // 緯度1次（40'単位換算）
  const lon1 = Number(s.slice(2, 4)); // 経度1次（1度単位のオフセット）
  if (!Number.isFinite(lat1) || !Number.isFinite(lon1)) return null;

  // 1次メッシュ南西
  const baseSouth = lat1 * (2 / 3); // lat = floor(lat*1.5) の逆：*2/3
  const baseWest = lon1 + 100;

  // 1次のサイズ
  const h1 = 2 / 3; // 40'
  const w1 = 1; // 1°

  // 2次の分割（8x8）
  const a = Number(s.slice(4, 5)); // 0..7
  const b = Number(s.slice(5, 6)); // 0..7
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  const h2 = h1 / 8; // 5' = 1/12
  const w2 = w1 / 8; // 7.5' = 1/8

  let south = baseSouth + a * h2;
  let west = baseWest + b * w2;
  let h = h2;
  let w = w2;

  if (s.length >= 8) {
    // 3次（2次を10x10）
    const c = Number(s.slice(6, 7)); // 0..9
    const d = Number(s.slice(7, 8)); // 0..9
    if (!Number.isFinite(c) || !Number.isFinite(d)) return null;

    const h3 = h2 / 10; // 30" = 1/120
    const w3 = w2 / 10; // 45" = 1/80

    south = south + c * h3;
    west = west + d * w3;
    h = h3;
    w = w3;
  }

  if (s.length === 9) {
    // 4次（3次を2x2、末尾q=1..4）
    const q = Number(s.slice(8, 9));
    if (![1, 2, 3, 4].includes(q)) return null;

    const h4 = h / 2; // 15" = 1/240
    const w4 = w / 2; // 22.5" = 1/160

    // q: 1=SW, 2=SE, 3=NW, 4=NE
    const addSouth = q === 3 || q === 4 ? h4 : 0;
    const addWest = q === 2 || q === 4 ? w4 : 0;

    south = south + addSouth;
    west = west + addWest;
    h = h4;
    w = w4;
  }

  return {
    west,
    south,
    east: west + w,
    north: south + h,
  };
}

function rectToPolygon(west, south, east, north) {
  return [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];
}

/** rows → GeoJSON（pickIdx=1 or 2：2列目or3列目） */
function buildMeshGeoJSON(rows, pickIdx, minValue) {
  const features = [];

  for (const r of rows) {
    const code = String(r?.[0] ?? "");
    if (!code) continue;

    const value = Number(r?.[pickIdx]);
    if (!Number.isFinite(value) || value <= minValue) continue;

    const b = meshCodeToBounds(code);
    if (!b) continue;

    features.push({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [rectToPolygon(b.west, b.south, b.east, b.north)],
      },
      properties: { v: value },
    });
  }

  return { type: "FeatureCollection", features };
}

/** 5段階グレーの fill layer を追加/更新 */
function addOrUpdateMeshLayer(map, sourceId, layerId, geojson, breaks, alpha = 1) {
  if (!map || !isMapStyleReady(map)) return;
  // メタデータが降順なので reverse() で step の昇順要件を満たす
  const [b1, b2, b3, b4] = breaks.map((x) => Number(x ?? 0)).reverse();

  const paint = {
    "fill-opacity": Number.isFinite(alpha) ? alpha : 1,
    "fill-outline-color": "rgba(0,0,0,0)",
    "fill-color": [
      "step",
      ["get", "v"],
      "#ffffff",
      b1, "#dddddd",
      b2, "#bbbbbb",
      b3, "#888888",
      b4, "#333333",
    ],
  };


  let hasSource = false;
  let hasLayer = false;
  try { hasSource = !!map.getSource(sourceId); } catch {
    //
  }
  try { hasLayer = !!map.getLayer(layerId); } catch {
    //
  }


  if (!hasSource) {
    try { map.addSource(sourceId, { type: "geojson", data: geojson }); } catch {
      //
    }
  } else {
    try { map.getSource(sourceId).setData(geojson); } catch {
      //
    }
  }

  // ✅ sourceはあるのにlayerが無い、を救済（style再読込など）
  if (!hasLayer) {
    try {
      map.addLayer({ id: layerId, type: "fill", source: sourceId, paint });
    } catch (e) {
      // style切替直後などで addLayer が通らない瞬間があるので黙殺
      // 次回 run() で復活する
    }
    return;
  }

  // ✅ paint を毎回戻す（style側の干渉を打ち消す）
  try { map.setPaintProperty(layerId, "fill-opacity", Number.isFinite(alpha) ? alpha : 1); } catch {
    //
  }
  try { map.setPaintProperty(layerId, "fill-outline-color", "#222222"); } catch {
    //
  }
  try { map.setPaintProperty(layerId, "fill-color", paint["fill-color"]); } catch {
    //
  }




}

/** 3マップ連動（move同期：無限ループ防止） */
function linkMaps(maps) {
  let syncing = false;

  const handlers = new Map();
  const syncFrom = (src) => {
    if (syncing) return;
    syncing = true;

    const c = src.getCenter();
    const z = src.getZoom();
    const b = src.getBearing();
    const p = src.getPitch();

    for (const m of maps) {
      if (m === src) continue;
      m.jumpTo({ center: c, zoom: z, bearing: b, pitch: p });
    }

    syncing = false;
  };

  for (const m of maps) {
    const fn = () => syncFrom(m);
    handlers.set(m, fn);
    m.on("move", fn);
  }

  return () => {
    for (const m of maps) {
      const fn = handlers.get(m);
      if (fn) {
        try { m.off("move", fn); } catch {
          //
        }
      }
    }
  };
}

// ================================
// ✅ ZIP export helpers
// ================================

function safeFileName(s) {
  return String(s ?? "")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Map canvas → PNG Blob（ZIPに入れる用） */
async function mapCanvasToPngBlob(map) {
  if (!map) throw new Error("map is null");

  // ✅ 描画が落ち着くのを待つ（タイル読み込み中だと空/途中になりやすい）
  await new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    // ✅ すでにidleっぽいと "idle" が二度と来ないことがあるのでフォールバック
    const fallback = setTimeout(finish, 800);

    const waitIdle = () => {
      try {
        map.once("idle", () => {
          clearTimeout(fallback);
          finish();
        });
        requestAnimationFrame(() => map.triggerRepaint());
      } catch {
        clearTimeout(fallback);
        finish();
      }
    };

    if (map.loaded()) {
      waitIdle();
    } else {
      try {
        map.once("load", waitIdle);
      } catch {
        clearTimeout(fallback);
        finish();
      }
    }
  });

  const canvas = map.getCanvas();
  const blob = await new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png"
      );
    } catch (e) {
      reject(e);
    }
  });

  return blob;
}

/** 凡例を自前描画してPNG Blob化（依存追加なし） */
function legendToPngBlob(effectiveBreaks, meta, opts = {}) {
  const breaks = Array.isArray(effectiveBreaks) ? effectiveBreaks : [1, 2, 3, 4];
  const b1 = breaks?.[0] ?? "";
  const b2 = breaks?.[1] ?? "";
  const b3 = breaks?.[2] ?? "";
  const b4 = breaks?.[3] ?? "";

  const title = String(opts.title ?? "凡例（グレースケール）");
  const subtitle = String(opts.subtitle ?? "");
  const layout = String(opts.layout ?? "horizontal"); // "horizontal" | "vertical"

  const items = [
    { c: "#ffffff", label: `≤ ${b1}` },
    { c: "#dddddd", label: `${b1} – ${b2}` },
    { c: "#bbbbbb", label: `${b2} – ${b3}` },
    { c: "#888888", label: `${b3} – ${b4}` },
    { c: "#333333", label: `> ${b4}` },
  ];

  const pad = 18;
  const isVertical = layout === "vertical";

  // ヨコ / タテでキャンバスを分ける
  const width = isVertical ? 520 : 980;
  const height = isVertical ? 320 : 190;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context not available");

  // 背景
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  // タイトル
  ctx.fillStyle = "#111111";
  ctx.font = "bold 16px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
  ctx.fillText(title, pad, 34);

  // サブ情報（任意）
  ctx.fillStyle = "#444444";
  ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";

  const hName = meta?.hanrei_height_name ?? "高さ（3列目）";
  const cName = meta?.hanrei_color_name ?? "色（2列目）";
  const line1 = `height: ${hName} / color: ${cName}`;
  ctx.fillText(line1, pad, 56);

  if (subtitle) {
    ctx.fillText(subtitle, pad, 74);
  }

  ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";

  if (!isVertical) {
    // ✅ ヨコ（従来）
    const chipY = subtitle ? 98 : 86;
    const chipH = 44;
    const chipW = 170;
    const gap = 10;

    items.forEach((it, i) => {
      const x = pad + i * (chipW + gap);

      // 外枠
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 1;
      roundRect(ctx, x, chipY, chipW, chipH, 10);
      ctx.stroke();

      // 色チップ
      ctx.fillStyle = it.c;
      ctx.strokeStyle = "#111111";
      roundRect(ctx, x + 10, chipY + 12, 18, 18, 4);
      ctx.fill();
      ctx.stroke();

      // ラベル
      ctx.fillStyle = "#111111";
      ctx.fillText(String(it.label), x + 36, chipY + 29);
    });
  } else {
    // ✅ タテ（新規）
    const startY = subtitle ? 98 : 86;
    const rowH = 40;
    const boxW = width - pad * 2;

    items.forEach((it, i) => {
      const y = startY + i * rowH;

      // 外枠
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 1;
      roundRect(ctx, pad, y, boxW, 34, 10);
      ctx.stroke();

      // 色チップ
      ctx.fillStyle = it.c;
      ctx.strokeStyle = "#111111";
      roundRect(ctx, pad + 10, y + 8, 18, 18, 4);
      ctx.fill();
      ctx.stroke();

      // ラベル
      ctx.fillStyle = "#111111";
      ctx.fillText(String(it.label), pad + 40, y + 21);
    });
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("legend toBlob returned null"))),
      "image/png"
    );
  });
}

// ================================
// ✅ existing single PNG helper (kept)
// ================================

async function downloadPng(map, filename) {
  if (!map) return;

  // ✅ 描画が落ち着くのを待つ（タイル読み込み中だと空/途中になりやすい）
  await new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const fallback = setTimeout(finish, 800);

    const waitIdle = () => {
      try {
        map.once("idle", () => {
          clearTimeout(fallback);
          finish();
        });
        requestAnimationFrame(() => map.triggerRepaint());
      } catch {
        clearTimeout(fallback);
        finish();
      }
    };

    if (map.loaded()) waitIdle();
    else {
      try { map.once("load", waitIdle); }
      catch {
        clearTimeout(fallback);
        finish();
      }
    }
  });

  const canvas = map.getCanvas();

  // ✅ CORS汚染などで失敗するので、例外を見える化 + toBlobで安定DL
  try {
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png"
      );
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;

    // DOMに入れた方が安定するブラウザがある
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  } catch (e) {
    console.warn("[PNG export failed]", e);

    const msg = String(e?.message ?? e);
    if (msg.toLowerCase().includes("security") || msg.toLowerCase().includes("tainted")) {
      alert(
        "PNG書き出しに失敗しました（CORSでcanvasが汚染されている可能性が高いです）。\n" +
          "対策：style/tiles/sprite/font を同一オリジン or CORS許可された経路にしてください。"
      );
    } else {
      alert("PNG書き出しに失敗しました: " + msg);
    }
  }
}

function forceMonochrome(map) {
  const style = map.getStyle();
  if (!style?.layers) return;

  style.layers.forEach((layer) => {
    const id = layer.id;

    // 背景
    if (layer.type === "background") {
      try {
        map.setPaintProperty(id, "background-color", "#ffffff");
      } catch {
        //
      }
    }

    // fill
    if (layer.type === "fill") {
      try {
        map.setPaintProperty(id, "fill-color", "#cccccc");
      } catch {
        //
      }
    }

    // line
    if (layer.type === "line") {
      // 道路はさらに薄く
      if (id.includes("road")) {
        map.setPaintProperty(id, "line-opacity", 0.2);
      } else {
        map.setPaintProperty(id, "line-opacity", 1);
      }
    }

    // symbol（文字）
    if (layer.type === "symbol") {
      try {
        map.setPaintProperty(id, "text-color", "#444444");
      } catch {
        //
      }
      try {
        map.setPaintProperty(id, "icon-color", "#444444");
      } catch {
        //
      }
    }

    // raster
    if (layer.type === "raster") {
      try {
        map.setPaintProperty(id, "raster-saturation", -1);
      } catch {
        //
      }
    }
  });
}

// ✅ ベース地図を薄くする（全レイヤーのopacityを下げる）
// - ベースだけに適用する想定（mesh枠線など後段で足すレイヤーには影響しない）
function lightenAllLayers(map, opacity = 0.35) {
  const style = map.getStyle();
  if (!style?.layers) return;

  const a = Math.max(0, Math.min(1, Number(opacity)));

  style.layers.forEach((layer) => {
    const id = layer.id;

    // ✅ 主役レイヤーは薄くしない（たまにメッシュまで薄くなる事故を防ぐ）
    // - 自作のメッシュ/学校など（必要なら追加）
    if (id.startsWith("mesh-") || id === "school-highlight") {
      return;
    }

    try {
      if (layer.type === "background") map.setPaintProperty(id, "background-opacity", a);
      if (layer.type === "fill") map.setPaintProperty(id, "fill-opacity", a);
      if (layer.type === "line") map.setPaintProperty(id, "line-opacity", a);
      if (layer.type === "symbol") {
        map.setPaintProperty(id, "text-opacity", a);
        map.setPaintProperty(id, "icon-opacity", a);
      }
      if (layer.type === "raster") map.setPaintProperty(id, "raster-opacity", a);
      if (layer.type === "hillshade") map.setPaintProperty(id, "hillshade-exaggeration", 0);
    } catch {
      // レイヤーによってはプロパティ未対応の場合があるので黙殺
    }
  });
}

function resetBaseStyle(map) {
  if (!map) return;
  const style = map.getStyle();
  if (!style?.layers) return;

  style.layers.forEach((layer) => {
    const id = layer.id;

    // mesh/学校は触らない
    if (id.startsWith("mesh-") || id === "school-highlight") return;

    try {
      if (layer.type === "background") {
        // opacity を戻す（未設定＝デフォルト扱い）
        map.setPaintProperty(id, "background-opacity", 1);
      }
      if (layer.type === "fill") {
        map.setPaintProperty(id, "fill-opacity", 1);
      }
      if (layer.type === "line") {
        map.setPaintProperty(id, "line-opacity", 1);
      }
      if (layer.type === "symbol") {
        map.setPaintProperty(id, "text-opacity", 1);
        map.setPaintProperty(id, "icon-opacity", 1);
      }
      if (layer.type === "raster") {
        map.setPaintProperty(id, "raster-opacity", 1);
        map.setPaintProperty(id, "raster-saturation", 0);
      }
      if (layer.type === "hillshade") {
        // exaggeration を戻す（0にしたのを復帰）
        map.setPaintProperty(id, "hillshade-exaggeration", 1);
      }
    } catch {
      //
    }
  });
}


function addSchoolOverlay(map) {
  if (!map) return;

  const style = map.getStyle();
  const sources = style?.sources || {};

  // vector source を1つ選ぶ（最初に見つかったもの）
  const vectorSourceId =
    Object.keys(sources).find((k) => sources?.[k]?.type === "vector") || null;

  if (!vectorSourceId) {
    console.warn("[school] no vector source found");
    return;
  }

  // source-layer 候補（地理院スタイルはここが揺れる）
  const sourceLayerCandidates = ["poi", "pois", "symbol", "poi_label", "label"];

  // 学校っぽい属性候補（スタイルによって違う）
  const filterCandidates = [
    ["==", ["get", "class"], "school"],
    ["==", ["get", "kind"], "school"],
    ["==", ["get", "category"], "school"],
    ["==", ["get", "subclass"], "school"],
    // “学校” が name に含まれるケース（日本語ラベル向け）
    ["in", "学校", ["get", "name"]],
  ];

  // 既にあれば二重追加しない
  if (map.getLayer("school-highlight")) return;

  for (const sourceLayer of sourceLayerCandidates) {
    try {
      for (const filter of filterCandidates) {
        map.addLayer({
          id: "school-highlight",
          type: "circle",
          source: vectorSourceId,
          "source-layer": sourceLayer,
          filter,
          paint: {
            "circle-radius": 4,
            "circle-color": "#111111",
            "circle-opacity": 0.85,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 1,
          },
        });

        console.log("[school] added", { vectorSourceId, sourceLayer, filter });
        return; // 追加できたら終了
      }
    } catch {
      // 次の候補へ
    }
  }

  console.warn("[school] could not add school layer (no match)");
}

export default function MapLibreTriple2D() {
  const unlinkRef = useRef(null);
  // /map2d/:mode/:no/:area  （modeは実質1固定運用でもOK）
  const { mode: urlMode, no: urlNo, area: urlArea } = useParams();
  const navigate = useNavigate();

  // ---- URLを唯一の真実として扱う（no/areaはstateを持たない）----
  const metas = data_meta || [];

  const metaNo = String(urlNo ?? metas?.[0]?.no ?? "");
  const meta = useMemo(
    () => metas.find((m) => String(m.no) === String(metaNo)) ?? metas?.[0],
    [metas, metaNo]
  );

  const area = useMemo(() => {
    const raw = urlArea ?? "福岡県糸島市";
    try {
      return decodeURI(String(raw));
    } catch {
      //
      return String(raw);
    }
  }, [urlArea]);

  const modeFixed = "1"; // コンポーネント側で固定運用
  const goUrl = (nextNo, nextArea) => {
    const n = String(nextNo || "");
    const a = encodeURIComponent(String(nextArea || ""));
    navigate(`/map2d/${modeFixed}/${n}/${a}`);
  };

  // ---- year / csvName はUIで選ぶので state（meta変更時にデフォルトに寄せる）----
  const [year, setYear] = useState("2021");
  const [csvName, setCsvName] = useState("全産業従業者数×全産業従業者数");

  const [alpha, setAlpha] = useState(1);
  const [shikii, setShikii] = useState([null, null, null, null]);

  // ---- Map refs ----
  const baseRef = useRef(null);
  const heightRef = useRef(null);
  const colorRef = useRef(null);

  const baseMapRef = useRef(null);
  const heightMapRef = useRef(null);
  const colorMapRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("");
  const autoRanRef = useRef(false);

  const [baseDesign, setBaseDesign] = useState("light"); // "light" | "dark"

  // 後着防止（世代）
  const genRef = useRef(0);

  // 年候補をメタデータから生成
  const yearOptions = useMemo(() => {
    if (!meta) return [];

    const s = new Set();

    const addNum = (v) => {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) s.add(String(n));
    };

    const addCsvList = (v) => {
      if (!v) return;
      String(v)
        .split(/[,\s]+/)
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach(addNum);
    };

    addCsvList(meta.year_display);
    addCsvList(meta.year_syousai);

    addNum(meta.initialYear);
    addNum(meta.years);
    addNum(meta.year1);
    addNum(meta.year2);
    addNum(meta.year3);
    addNum(meta.year4);
    addNum(meta.year5);
    addNum(meta.year6);
    addNum(meta.year7);
    addNum(meta.year8);
    addNum(meta.year9);
    addNum(meta.year10);
    addNum(meta.year11);
    addNum(meta.year12);

    return Array.from(s).sort((a, b) => Number(a) - Number(b));
  }, [meta]);

  // metaNo（=URLのno）が変わったら、year/csvName を meta デフォルトに寄せる
  useEffect(() => {
    if (!meta) return;

    const defaultYear =
      meta.initialYear ??
      meta.years ??
      meta.year5 ??
      meta.year4 ??
      meta.year3 ??
      meta.year2 ??
      meta.year1;

    const defaultCsv =
      meta.csv_filename ??
      meta.csv_filename2 ??
      meta.csv_name ??
      meta.csvName ??
      meta.csv;

    if (defaultCsv != null && String(defaultCsv).length) {
      setCsvName(String(defaultCsv));
    }
    if (defaultYear != null && String(defaultYear).length) {
      setYear(String(defaultYear));
    }

    // テーマを切り替えたら「初回自動run」をもう一度許可
    autoRanRef.current = false;
  }, [metaNo, meta]);

  // year が候補から外れたら補正
  useEffect(() => {
    if (yearOptions.length === 0) return;
    if (!yearOptions.includes(String(year))) {
      setYear(yearOptions[0]);
    }
  }, [yearOptions, year]);

  // ---- init maps ----
  useEffect(() => {
    if (!baseRef.current || !heightRef.current || !colorRef.current) return;
    if (baseMapRef.current || heightMapRef.current || colorMapRef.current) return;

    const common = {
      style: GSI_STYLE_URL,
      center: [130.2, 33.6],
      zoom: 10,
      preserveDrawingBuffer: true,
    };

    const baseMap = new maplibregl.Map({ container: baseRef.current, ...common });
    const heightMap = new maplibregl.Map({ container: heightRef.current, ...common });
    const colorMap = new maplibregl.Map({ container: colorRef.current, ...common });

    baseMapRef.current = baseMap;
    heightMapRef.current = heightMap;
    colorMapRef.current = colorMap;

    let loaded = 0;
    const onLoad = () => {
      loaded += 1;
      if (loaded === 3) {
        forceMonochrome(baseMap);
        forceMonochrome(heightMap);
        forceMonochrome(colorMap);
        // ✅ ベースだけ薄くする（海や道路が主張しすぎないように）
        lightenAllLayers(baseMap, 0.35);
        lightenAllLayers(heightMap, 0.2);
        lightenAllLayers(colorMap, 0.2);
        // ✅ 学校をベース地図に上乗せ（黒点＋白縁）
        addSchoolOverlay(baseMap);
        unlinkRef.current = linkMaps([baseMap, heightMap, colorMap]);
        setReady(true);

        // // loaded === 3 の中（setReady(true) の前後どちらでもOK）
        // window.__ml = {
        //   base: baseMap,
        //   height: heightMap,
        //   color: colorMap,
        // };

        setStatus("ready");
      }
    };

    baseMap.on("load", onLoad);
    heightMap.on("load", onLoad);
    colorMap.on("load", onLoad);

    return () => {
      try { unlinkRef.current?.(); } catch {
        //
      }
      unlinkRef.current = null;
      try { baseMap.remove(); } catch {
        //
      }
      try { heightMap.remove(); } catch {
        //
      }
      try { colorMap.remove(); } catch {
        //
      }
      baseMapRef.current = null;
      heightMapRef.current = null;
      colorMapRef.current = null;
    };
  }, []);

  const effectiveBreaks = useMemo(() => {
    if (!meta) return [1, 2, 3, 4];
    const b1 = Number(shikii[0] ?? meta.shikii1 ?? 0);
    const b2 = Number(shikii[1] ?? meta.shikii2 ?? 0);
    const b3 = Number(shikii[2] ?? meta.shikii3 ?? 0);
    const b4 = Number(shikii[3] ?? meta.shikii4 ?? 0);
    return [b1, b2, b3, b4];
  }, [meta, shikii]);

  const run = async () => {
    const heightMap = heightMapRef.current;
    const colorMap = colorMapRef.current;
    const baseMap = baseMapRef.current;

    if (!ready || !meta || !heightMap || !colorMap) return;
    if (!area || !csvName || !year) return;

    // ✅ style が “まだ” の時に return してしまうと、その1回が空振りになって以後描画されない。
    //    → タイムアウト付きで「待つ」実装にする（原因究明にも役立つ）
    const waitForStyleReady = (map, label, timeoutMs = 2500) => {
      if (!map) return Promise.reject(new Error(`${label}: map is null`));

      const isReady = () => {
        try {
          if (typeof map.isStyleLoaded === "function") return !!map.isStyleLoaded();
          if (typeof map.loaded === "function") return !!map.loaded();
        } catch {
          //
        }
        return false;
      };

      if (isReady()) return Promise.resolve();

      return new Promise((resolve, reject) => {
        let done = false;

        const finishOk = () => {
          if (done) return;
          done = true;
          cleanup();
          resolve();
        };
        const finishNg = (err) => {
          if (done) return;
          done = true;
          cleanup();
          reject(err);
        };

        const onStyleData = () => {
          if (isReady()) finishOk();
        };

        const timer = setTimeout(() => {
          finishNg(new Error(`${label}: style not ready (timeout ${timeoutMs}ms)`));
        }, timeoutMs);

        const cleanup = () => {
          clearTimeout(timer);
          try { map.off("styledata", onStyleData); } catch {
            //
          }
          try { map.off("load", onStyleData); } catch {
            //
          }
          try { map.off("idle", onStyleData); } catch {
            //
          }
        };

        // “いつ揃うか” が揺れるので、複数イベントで拾う
        try { map.on("styledata", onStyleData); } catch {
          //
        }
        try { map.on("load", onStyleData); } catch {
          //
        }
        try { map.on("idle", onStyleData); } catch {
          //
        }

        // 念のため最初のチェックももう一回
        onStyleData();
      });
    };

    // ✅ 診断しやすいように世代を先に確保
    const myGen = ++genRef.current;
    setStatus("loading...");

    try {
      // ✅ 3つとも style-ready になるまで待つ（待ってる間にパラメータが変わったら中断）
      setStatus("loading... (waiting style)");
      await Promise.all([
        waitForStyleReady(baseMap, "baseMap"),
        waitForStyleReady(heightMap, "heightMap"),
        waitForStyleReady(colorMap, "colorMap"),
      ]);
      if (genRef.current !== myGen) return;

      // ✅ CSV fetch
      setStatus("loading... (fetch csv)");
      const urls = buildCsvUrls(meta.no, area, csvName, year);
      const csvText = await fetchCombinedCsv(urls);
      if (genRef.current !== myGen) return;

      const rows = parseCsvToRows(csvText);
      if (!rows?.[0]?.[0] || String(rows[0][0]).startsWith("<")) {
        setStatus("CSVが不正（HTMLが返っている可能性）");
        return;
      }

      const codeLen = String(rows[0][0]).length;
      const min =
        codeLen === 6
          ? Number(meta.kokudo_min ?? meta.min ?? 0)
          : Number(meta.min ?? 0);

      const clean = rows.filter((r) => Array.isArray(r) && r.length > 2 && r[0]);

      // ✅ GeoJSON構築
      setStatus("loading... (build geojson)");
      const heightGeo = buildMeshGeoJSON(clean, 2, min);
      const colorGeo = buildMeshGeoJSON(clean, 1, min);

      // ✅ remove / add の安全ユーティリティ（黙殺しすぎない）
      const safeRemoveLayer = (map, layerId) => {
        if (!map) return;
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[removeLayer failed]", layerId, e);
          }
        }
      };
      const safeRemoveSource = (map, sourceId) => {
        if (!map) return;
        try {
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[removeSource failed]", sourceId, e);
          }
        }
      };
      const resetLayer = (map, sourceId, layerId) => {
        safeRemoveLayer(map, layerId);
        safeRemoveSource(map, sourceId);
      };

      // ✅ baseMap の枠線（line）を作り直す
      setStatus("loading... (apply base layer)");
      resetLayer(baseMap, "mesh-base-src", "mesh-base-line");

      // ✅ 枠線paint（ベースと塗りで共通に使う）
      const meshLinePaint = {
        "line-color": "#222222",
        // ✅ 縮尺（zoom）に応じて自然に太さを変える
        // 低zoom: ほぼ線が主張しない / 高zoom: マス目が読みやすい太さ
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          6,  0.05,
          8,  0.10,
          10, 0.20,
          12, 0.45,
          14, 0.70,
          16, 1.05
        ],
        "line-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          6,  0.35,
          10, 0.55,
          12, 0.75,
          16, 0.90
        ],
      };

      try {
        baseMap.addSource("mesh-base-src", { type: "geojson", data: heightGeo });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[base addSource failed]", e);
        }
      }

      try {
        baseMap.addLayer({
          id: "mesh-base-line",
          type: "line",
          source: "mesh-base-src",
          paint: meshLinePaint,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[base addLayer failed]", e);
        }
      }

      // ✅ fill（高さ/色）は addOrUpdateMeshLayer 側に寄せる（style-ready前提）
      setStatus("loading... (apply mesh layers)");
      resetLayer(heightMap, "mesh-height-src", "mesh-height-fill");
      safeRemoveLayer(heightMap, "mesh-height-line"); // ✅ 追加
      resetLayer(colorMap, "mesh-color-src", "mesh-color-fill");
      safeRemoveLayer(colorMap, "mesh-color-line");   // ✅ 追加

      addOrUpdateMeshLayer(
        heightMap,
        "mesh-height-src",
        "mesh-height-fill",
        heightGeo,
        effectiveBreaks,
        Number(alpha)
      );
      addOrUpdateMeshLayer(
        colorMap,
        "mesh-color-src",
        "mesh-color-fill",
        colorGeo,
        effectiveBreaks,
        Number(alpha)
      );

      // ✅ 塗り側にもベースと同じ枠線（line）を重ねる
      try {
        heightMap.addLayer({
          id: "mesh-height-line",
          type: "line",
          source: "mesh-height-src",
          paint: meshLinePaint,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[height addLayer line failed]", e);
        }
      }
      try {
        colorMap.addLayer({
          id: "mesh-color-line",
          type: "line",
          source: "mesh-color-src",
          paint: meshLinePaint,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[color addLayer line failed]", e);
        }
      }

      // ✅ メッシュ範囲へ自動ズーム
      if (heightGeo?.features?.length > 0) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        for (const f of heightGeo.features) {
          const coords = f.geometry.coordinates[0];
          for (const [x, y] of coords) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }

        const bounds = [[minX, minY], [maxX, maxY]];

        baseMap.fitBounds(bounds, { padding: 40, duration: 600 });
        heightMap.fitBounds(bounds, { padding: 40, duration: 600 });
        colorMap.fitBounds(bounds, { padding: 40, duration: 600 });
      }

      setStatus(
        `done (height:${heightGeo.features.length}, color:${colorGeo.features.length}, codeLen:${codeLen})`
      );
    } catch (e) {
      // ✅ “何待ちで落ちたか” が status とログで分かる
      console.warn("[MapLibreTriple2D] run failed", e);
      setStatus(`error: ${e?.message ?? String(e)}`);
    }
  };


  // ✅ year/csvName/no/area が変わったら自動再描画（=同期）
  useEffect(() => {
    if (!ready || !meta) return;
    if (!year || !csvName || !area || !metaNo) return;
    run();
  }, [ready, metaNo, area, year, csvName]);

  // ✅ ページ読み込み時も自動でCSV読み込み（テーマ切替後も1回だけ許可）
  useEffect(() => {
    if (!ready || !meta) return;
    if (!year || !csvName || !area || !metaNo) return;
    if (autoRanRef.current) return;
    autoRanRef.current = true;
    run();
  }, [ready, metaNo, area, year, csvName]);

  // ✅ base/height/color/legend をzipにまとめて保存
  const exportAll = async () => {
    const baseMap = baseMapRef.current;
    const heightMap = heightMapRef.current;
    const colorMap = colorMapRef.current;
    if (!baseMap || !heightMap || !colorMap) return;

    try {
      const zip = new JSZip();

      const [baseBlob, heightBlob, colorBlob] = await Promise.all([
        mapCanvasToPngBlob(baseMap),
        mapCanvasToPngBlob(heightMap),
        mapCanvasToPngBlob(colorMap),
      ]);

      const title = "凡例（グレースケール）";
      const subtitle = `no:${metaNo} / year:${year} / area:${area}`;

      const legendBlobYoko = await legendToPngBlob(effectiveBreaks, meta, {
        title,
        subtitle,
        layout: "horizontal",
      });
      const legendBlobTate = await legendToPngBlob(effectiveBreaks, meta, {
        title,
        subtitle,
        layout: "vertical",
      });

      const baseName = safeFileName("ベースマップ") + ".png";
      const heightName = safeFileName(meta?.hanrei_height_name ?? "height") + ".png";
      const colorName = safeFileName(meta?.hanrei_color_name ?? "color") + ".png";
      const legendYokoName = safeFileName("凡例ヨコ") + ".png";
      const legendTateName = safeFileName("凡例タテ") + ".png";

      zip.file(baseName, baseBlob);
      zip.file(heightName, heightBlob);
      zip.file(colorName, colorBlob);
      zip.file(legendYokoName, legendBlobYoko);
      zip.file(legendTateName, legendBlobTate);

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const zipName = safeFileName(`map2d_${metaNo}_${year}_${area}_pngs.zip`);
      saveAs(zipBlob, zipName);
    } catch (e) {
      console.warn("[ZIP export failed]", e);
      alert("ZIP書き出しに失敗しました: " + String(e?.message ?? e));
    }
  };

  return (
    <div style={{ padding: 12 }} className="bg-white">



      {/* --- 上部：最小限（本番UI想定） --- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          テスト問題用白黒二次元表示版
        </div>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          テーマ：
          <select
            value={String(metaNo)}
            onChange={(e) => {
              const nextNo = e.target.value;
              goUrl(nextNo, area);
            }}
          >
            {metas
              .filter((m) => String(m.site) === "g")
              .map((m) => (
                <option key={String(m.no)} value={String(m.no)}>
                  {String(m.no)} {m.title ? `- ${m.title}` : ""}
                </option>
              ))}
          </select>
        </label>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          year：
          <select
            value={String(year)}
            onChange={(e) => setYear(e.target.value)}
            disabled={yearOptions.length === 0}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        {/* ✅ 追加：ベースデザイン切替（A案：dark=元スタイル） */}
        {/* <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          ベースデザイン：
          <select
            value={baseDesign}
            onChange={(e) => setBaseDesign(e.target.value)}
          >
            <option value="light">淡色（モノクロ＋薄め）</option>
            <option value="dark">渋め（元スタイル）</option>
          </select>
        </label> */}

        <button
          onClick={exportAll}
          disabled={!ready}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            cursor: ready ? "pointer" : "not-allowed",
            whiteSpace: "nowrap",
          }}
        >
          PNG（ZIP）
        </button>
      </div>






      {/* --- MAP（先に地図を出す） --- */}
      <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>
            地図（ベース）
          </div>
          <div
            ref={baseRef}
            style={{ height: "70vh", borderRadius: 8, overflow: "hidden" }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>
            {meta?.hanrei_color_name ?? "色（2列目）"}
          </div>
          <div
            ref={heightRef}
            style={{ height: "70vh", borderRadius: 8, overflow: "hidden" }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>
            {meta?.hanrei_height_name ?? "高さ（3列目）"}
          </div>
          <div
            ref={colorRef}
            style={{ height: "70vh", borderRadius: 8, overflow: "hidden" }}
          />
        </div>
      </div>

      {/* --- 凡例的（グレースケール四角＋閾値） --- */}
      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          background: "#fafafa",
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>
          凡例（グレースケール）
        </div>

        {/* 5段階の色チップ */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {[
            { c: "#ffffff", label: `≤ ${effectiveBreaks?.[0] ?? ""}` },
            {
              c: "#dddddd",
              label: `${effectiveBreaks?.[0] ?? ""} – ${effectiveBreaks?.[1] ?? ""}`,
            },
            {
              c: "#bbbbbb",
              label: `${effectiveBreaks?.[1] ?? ""} – ${effectiveBreaks?.[2] ?? ""}`,
            },
            {
              c: "#888888",
              label: `${effectiveBreaks?.[2] ?? ""} – ${effectiveBreaks?.[3] ?? ""}`,
            },
            { c: "#333333", label: `> ${effectiveBreaks?.[3] ?? ""}` },
          ].map((t, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "2px 6px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "#fff",
              }}
              title={t.label}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  background: t.c,
                  border: "1px solid #111",
                  display: "inline-block",
                  borderRadius: 3,
                }}
              />
              <span style={{ fontSize: 12, color: "#444" }}>{t.label}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {/* shikii入力（開発・調整用） */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "#666" }}>
            shikii（閾値）※空ならmetadata：
          </div>

          {["1", "2", "3", "4"].map((k, i) => (
            <label key={k} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              s{k}
              <input
                placeholder={String(
                  [meta?.shikii1, meta?.shikii2, meta?.shikii3, meta?.shikii4]?.[i] ?? ""
                )}
                value={shikii[i] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setShikii((prev) => {
                    const next = [...prev];
                    next[i] = v === "" ? null : Number(v);
                    return next;
                  });
                }}
                style={{ width: 90 }}
              />
            </label>
          ))}

          <div style={{ fontSize: 12, color: "#666" }}>
            breaks: [{effectiveBreaks.join(", ")}]
          </div>
        </div>
      </div>

      {/* --- 折りたたみ（開発用・本番では削除） --- */}
      <details style={{ marginTop: 10 }}>
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            color: "#444",
            userSelect: "none",
            padding: "6px 8px",
            borderRadius: 6,
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            display: "inline-block",
          }}
        >
          開発用、本番では削除
        </summary>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            padding: 10,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#fff",
          }}
        >
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            area（&可）：
            <input
              defaultValue={area}
              onBlur={(e) => goUrl(metaNo, e.target.value)}
              style={{ width: 220 }}
              placeholder="例: 福岡県糸島市"
            />
          </label>

          {/* csv_nameはmeta上でnoと1対1対応なので、開発用にだけ残す */}
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            csv_name：
            <input
              value={csvName}
              onChange={(e) => setCsvName(e.target.value)}
              style={{ width: 240 }}
            />
          </label>

          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            alpha：
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={alpha}
              onChange={(e) => setAlpha(e.target.value)}
              style={{ width: 70 }}
            />
          </label>

          <button
            onClick={run}
            disabled={!ready}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              cursor: ready ? "pointer" : "not-allowed",
            }}
          >
            CSV読込 → グレー表示
          </button>
        </div>
      </details>

      <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
        メモ：
        <ul style={{ marginTop: 6 }}>
          <li>
            CSVは{" "}
            <code>
              /csv/{`{meta.no}`}/{`{area}`}_{`{csv_name}`}_{`{year}`}.csv
            </code>{" "}
            をfetch（areaに <code>&amp;</code> があれば複数結合）
          </li>
          <li>矩形は標準地域メッシュ（2次/3次/4次）として bounds を生成して塗りつぶし</li>
          <li>PNG出力は <code>preserveDrawingBuffer: true</code> 前提</li>
        </ul>
      </div>
    </div>
  );
}
